import { useMutation } from '@tanstack/react-query'
import { FormattedMessage } from 'react-intl'

import { downloadTaxForm } from '@/domains/Contractor/api'
import { ContractorDetails } from '@/domains/Contractor/types'
import { useErrorToast } from '@/hooks/useErrorToast'
import { downloadFile, formatTaxForm, getAnimationKey } from '@/lib/utils'
import { CountryName, Widget } from '@/shared/components'
import { Document } from '@/shared/icons/solid'
import { Button, Details, MotionDiv } from '@/shared/ui'

type Props = {
  contractor?: ContractorDetails
  isPending: boolean
}

export const ExternalContractorTaxInfo = ({ contractor, isPending }: Props) => {
  const notifyError = useErrorToast()

  const { mutateAsync, isPending: isDownloading } = useMutation({
    mutationFn: downloadTaxForm,
  })

  const handleDownloadTaxForm = async () => {
    if (!contractor?.taxDetails.taxFormId) return

    try {
      const response = await mutateAsync({
        taxFormId: contractor.taxDetails.taxFormId,
      })

      downloadFile(response.data, `${contractor.taxDetails.taxFormType}.pdf`)
    } catch (error) {
      notifyError(error)
    }
  }

  return (
    <Widget
      title={
        <FormattedMessage
          defaultMessage="Tax information"
          id="label.taxInformation"
        />
      }
    >
      <MotionDiv
        key={getAnimationKey(isPending)}
        className="flex flex-col gap-6"
      >
        <Details>
          <Details.Label>
            <FormattedMessage
              defaultMessage="Tax residence"
              id="label.taxResidence"
            />
          </Details.Label>

          {contractor ? (
            <CountryName
              countryCode={contractor.taxDetails.countryOfTaxResidence}
            />
          ) : (
            <Details.Skeleton />
          )}
        </Details>
        <Details>
          <Details.Label>
            <FormattedMessage
              defaultMessage="Tax identification number"
              id="label.taxIdentificationNumber"
            />
          </Details.Label>
          {contractor ? (
            <Details.Value>{contractor.taxDetails.taxId ?? '-'}</Details.Value>
          ) : (
            <Details.Skeleton />
          )}
        </Details>
        <Details>
          <Details.Label>
            <FormattedMessage defaultMessage="Tax form" id="label.taxForm" />
          </Details.Label>

          {contractor ? (
            contractor.taxDetails.taxFormType ? (
              <Button
                variant="ghost"
                size="inline"
                className="underline"
                onClick={handleDownloadTaxForm}
                disabled={isDownloading}
                loaderPosition="left"
                loading={isDownloading}
                leftIcon={<Document className="size-4" />}
              >
                {formatTaxForm(contractor.taxDetails.taxFormType)}
              </Button>
            ) : (
              '-'
            )
          ) : (
            <Details.Skeleton />
          )}
        </Details>
      </MotionDiv>
    </Widget>
  )
}
